import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useGetIdentity } from '@refinedev/core';
import type { OtdUserSession } from '@/providers/authProvider';

const USER_CONTEXT_STORAGE_KEY = 'otd_user_context';

export interface UserData {
  product: string;
  region: string;
  role: string;
  userId: string;
  email: string;
}

interface UserContextValue {
  userData: UserData;
  setProduct: (product: string) => void;
  setRegion: (region: string) => void;
  setRole: (role: string) => void;
  getBusinessUnit: () => string;
  isLoading: boolean;
}

const defaultUserData: UserData = {
  product: '',
  region: '',
  role: '',
  userId: '',
  email: '',
};

const loadStoredUserContext = (): Pick<UserData, 'product' | 'region' | 'role' | 'userId' | 'email'> => {
  try {
    const raw = localStorage.getItem(USER_CONTEXT_STORAGE_KEY);
    if (!raw) {
      return { product: '', region: '', role: '', userId: '', email: '' };
    }

    const parsed = JSON.parse(raw) as Partial<UserData>;
    return {
      product: typeof parsed.product === 'string' ? parsed.product : '',
      region: typeof parsed.region === 'string' ? parsed.region : '',
      role: typeof parsed.role === 'string' ? parsed.role : '',
      userId: typeof parsed.userId === 'string' ? parsed.userId : '',
      email: typeof parsed.email === 'string' ? parsed.email : '',
    };
  } catch {
    return { product: '', region: '', role: '', userId: '', email: '' };
  }
};

const UserContext = createContext<UserContextValue>({
  userData: defaultUserData,
  setProduct: () => {},
  setRegion: () => {},
  setRole: () => {},
  getBusinessUnit: () => '',
  isLoading: true,
});

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { data: identity, isLoading } = useGetIdentity<OtdUserSession>();
  const [userData, setUserData] = useState<UserData>(() => {
    const stored = loadStoredUserContext();
    return {
      ...defaultUserData,
      ...stored,
    };
  });

  useEffect(() => {
    if (identity) {
      setUserData((prev) => ({
        product: identity.product || prev.product,
        region: identity.region || prev.region,
        role: identity.role || prev.role,
        userId: identity.userId || prev.userId,
        email: identity.email || prev.email,
      }));
    }
  }, [identity]);

  useEffect(() => {
    const payload = {
      product: userData.product,
      region: userData.region,
      role: userData.role,
      userId: userData.userId,
      email: userData.email,
    };
    localStorage.setItem(USER_CONTEXT_STORAGE_KEY, JSON.stringify(payload));
  }, [userData.product, userData.region, userData.role, userData.userId, userData.email]);

  const setProduct = useCallback((product: string) => {
    setUserData((prev) => ({ ...prev, product }));
  }, []);

  const setRegion = useCallback((region: string) => {
    setUserData((prev) => ({ ...prev, region }));
  }, []);

  const setRole = useCallback((role: string) => {
    setUserData((prev) => ({ ...prev, role }));
  }, []);

  const getBusinessUnit = useCallback(
    () => userData.region || userData.product,
    [userData.region, userData.product],
  );

  return (
    <UserContext.Provider
      value={{ userData, setProduct, setRegion, setRole, getBusinessUnit, isLoading }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
